import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import { Button, Flex, Box, Spinner } from '@radix-ui/themes';

import { StatusBadge } from '../shared/StatusBadge.jsx';

import { useBroadcastContext } from '../contexts/BroadcastContext.jsx';

import { REVIEW, AUDIENCE, FREE, EMAIL, SMS, BOTH } from '../utils/constants';

import { SendConfirmationModal } from './SendConfirmationModal.jsx';
import { ConfirmationModal } from './ConfirmationModal.jsx';
import { ScheduleModal } from './ScheduleModal.jsx';
import { SaveDraftButton } from './SaveDraftButton.jsx';

export const Footer = () => {
  const { t } = useTranslation('broadcasts');
  const {
    values,
    isValid,
    goToStep,
    sendBroadcast,
    scheduleBroadcast,
    saveAsDraft,
    beforeLastStep,
    isSending,
    canSendBroadcast,
    isLimitReached,
    tier,
    errors,
    emailCount,
    smsCount,
  } = useBroadcastContext();
  const { currentStep } = values;
  const [openModal, setOpenModal] = useState(false);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [openDraftModal, setOpenDraftModal] = useState(false);
  const [countError, setCountError] = useState('');

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  useEffect(() => {
    const { broadcastType } = values;

    if (broadcastType === BOTH) {
      if (!emailCount && !smsCount) return setCountError('both');
      if (!emailCount) return setCountError('email');
      if (!smsCount) return setCountError('sms');
    }

    if (broadcastType === EMAIL && !emailCount) return setCountError('email');
    if (broadcastType === SMS && !smsCount) return setCountError('sms');

    return setCountError('');
  }, [emailCount, smsCount, values, values.broadcastType]);

  const modalOnSaveClick = () => {
    if (!isValid) return;

    sendBroadcast();
    setOpenModal(false);
  };

  const disableNextBtn = () => {
    if (!isValid) return true;
    if (countError) return true;
    if (currentStep === REVIEW && isLimitReached && !canSendBroadcast)
      return true;

    return false;
  };

  const renderModal = () => {
    if (canSendBroadcast && tier === FREE) {
      return (
        <ConfirmationModal
          header={
            <>
              {t('confirmationModal.free.header')} <StatusBadge status={FREE} />
            </>
          }
          body={t('confirmationModal.body')}
          cancelText={t('confirmationModal.cancel')}
          submitText={
            <>
              <i
                className="fa-regular fa-paper-plane-top fa-lg"
                style={{ marginRight: '5px', transform: 'rotate(315deg)' }}
              />{' '}
              {t('confirmationModal.submit')}
            </>
          }
          open={openModal}
          onClose={() => setOpenModal(false)}
          onSave={modalOnSaveClick}
        />
      );
    }

    return (
      <SendConfirmationModal
        broadcastType={values.broadcastType}
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={modalOnSaveClick}
      />
    );
  };

  const showErrorTooltip = () => {
    if (countError) {
      switch (countError) {
        case 'email':
          return t('validation.emailCountError');
        case 'sms':
          return t('validation.smsCountError');
        default:
          return t('validation.smsAndEmailCountError');
      }
    }
    if (currentStep === REVIEW && isLimitReached && !canSendBroadcast) {
      return t('validation.freeBroadcastLimitReached');
    }

    if (Object.keys(errors).length) return Object.values(errors)[0];

    return '';
  };

  const handleSchedule = scheduleData => {
    scheduleBroadcast(scheduleData);
  };

  const nextBtn = () => {
    if (currentStep === REVIEW) {
      if (isSending) return <Spinner size="3" />;

      const isDisabled = disableNextBtn();

      return (
        <Box data-tip={showErrorTooltip()} style={{ display: 'inline-block' }}>
          <Flex gap="2">
            <Button
              size="3"
              variant="outline"
              disabled={isDisabled}
              onClick={() => setOpenDraftModal(true)}
            >
              <i className="fa-regular fa-floppy-disk" />
              {t('saveAsDraft')}
            </Button>
            <Button
              size="3"
              variant="outline"
              disabled={isDisabled}
              onClick={() => setOpenModal(true)}
            >
              <i
                className="fa-regular fa-paper-plane-top"
                style={{ transform: 'rotate(315deg)' }}
              />
              {t('scheduling.sendNow')}
            </Button>
            <Button
              size="3"
              variant="solid"
              disabled={isDisabled}
              onClick={() => setOpenScheduleModal(true)}
            >
              <i className="fa-regular fa-calendar-clock" />
              {t('scheduling.scheduleForLater')}
            </Button>
          </Flex>
        </Box>
      );
    }

    return (
      <Box data-tip={showErrorTooltip()} style={{ display: 'inline-block' }}>
        <Button
          size="3"
          variant="solid"
          disabled={disableNextBtn()}
          onClick={() => goToStep(1)}
        >
          {currentStep === beforeLastStep() ? t('review') : t('nextStep')}
        </Button>
      </Box>
    );
  };

  return (
    <Flex justify="between" my="4">
      {currentStep === REVIEW && renderModal()}
      {currentStep === REVIEW && (
        <ScheduleModal
          open={openScheduleModal}
          onClose={() => setOpenScheduleModal(false)}
          onSchedule={handleSchedule}
        />
      )}
      {currentStep === REVIEW && (
        <SaveDraftButton
          open={openDraftModal}
          onClose={() => setOpenDraftModal(false)}
          onSave={date => saveAsDraft(date)}
        />
      )}
      <Button
        size="3"
        variant="outline"
        color="gray"
        onClick={() => goToStep(-1)}
        disabled={currentStep === AUDIENCE}
      >
        {t('previous')}
      </Button>
      {nextBtn()}
    </Flex>
  );
};
